<template>
  <NuxtLayout #="{ showModalMessages }">
    <JsonLDCollection :jsonOptions="jsonCollectionOptions" />
    <div class="slot-body">
      <h1 class="visually-hidden">{{ categoryH1 }}</h1>
      <div v-if="!isMobile()" class="box-gray"></div>
      <div class="container">
        <div class="row pt-5 align-items-center">
          <div class="col-lg-2 col-4">
            <div class="title mt-4">
              {{ translate.title }}
            </div>
          </div>
          <div class="col-lg-5 col-1">
            <span class="subtitle">
              {{ translate.subTitle }}
            </span>
          </div>
          <div
            class="col-lg-4 col-7 offset-lg-1 d-flex align-items-center box-input"
          >
            <input
              type="text"
              name="search"
              id="search-columns"
              :placeholder="translate.searchPlaceholder"
              v-model="search"
              @keyup.enter="searchColumns(showModalMessages)"
              @keyup="clearSearchText"
            />
            <font-awesome
              :icon="['fas', 'search']"
              size="sm"
              @click="searchColumns(showModalMessages)"
            />
          </div>
        </div>
        <div class="mt-4" v-if="!searchText">
          <ColunasHome
            :title-visible="false"
            :button-mobile="false"
            :button-more="false"
            @getArticleTagId="getArticleTagId"
          />
        </div>
        <div class="mt-3">
          <div v-if="searchText">
            <div class="search-div" v-if="searchTextKeyUp">
              <br />{{ translate.searchTerm }} <strong>{{ search }}</strong
              ><br /><br />
            </div>
            <div v-if="searchTextKeyUp && !postColumns.length">
              {{ translate.searchNoRecords }}<br /><br />
            </div>
          </div>
          <ColumnsCardsMobile :posts="postColumns" v-if="isMobile()" />
          <ColumnsCards :posts="postColumns" v-else />
        </div>
        <div
          class="row d-flex align-items-center justify-content-center"
          v-if="postColumns.length >= limit"
        >
          <button
            type="button"
            class="more-blog my-5 p-3 col-lg-3 col-8"
            @click="readMore"
          >
            {{ translate.readMore }}
            <font-awesome :icon="['fas', 'arrow-down']" size="md" />
          </button>
        </div>
        <div
          class="row mt-3 pb-5 d-flex align-items-center justify-content-center"
        >
          <BannerAssineHorizontal :path="bannerAssineHorizontal" />
        </div>
      </div>
    </div>
  </NuxtLayout>
</template>

<script lang="ts" setup>
import translaters from "@/tradutors/columns";
import ColunasHome from "@/components/HomePage/ColunasHome.vue";
import ColumnsCards from "@/components/ColumnsCards/Cards.vue";
import ColumnsCardsMobile from "@/components/ColumnsCards/CardsMobile.vue";
import { isMobile } from "@/shared/utils/mobile";
import api from "@/api";
import BannerAssineHorizontal from "@/components/HomePage/BannerAssineHorizontal.vue";
import {
  decidirPeloId,
  decidirPelaCategoria,
  lerRespostaColunas,
  semArtigo,
} from "@/utils/colunasCategoria";
import { TIMEOUT_SSR_MS } from "@/utils/ensaioSSR";

definePageMeta({
  middleware: [
    "not-found-lang",
    // Antes da API: id que não é número não existe, e o 9999 (o "todas" da
    // busca) é a listagem geral.
    (to) => {
      const decisao = decidirPeloId(String(to.params.catergoriaId));
      if (decisao.tipo === "404") {
        throw createError({ statusCode: 404, statusMessage: "Page Not Found" });
      }
      if (decisao.tipo === "todas") {
        return navigateTo(
          {
            name: "language-colunas",
            params: { language: to.params.language },
            query: to.query,
          },
          { redirectCode: 301 }
        );
      }
    },
  ],
});
const route = useRoute();
const canonical = useCanonicalUrl();
const router = useRouter();
const translate = computed(() =>
  route.params.language === "pt-br" ? translaters.portugues : translaters.ingles
);
const storeLocale = localeStore();

const seo = await useSeoConfig(String(route.name), String(route.params.language));

const articleTagId = ref(route.params.catergoriaId);
const outroIdioma = route.params.language === "pt-br" ? "en" : "pt-br";

const { data, error } = await useAsyncData("getColumns"+ "-" + route.params.language+ "-" + route.params.catergoriaId, async () => {
  // A categoria no outro idioma, em paralelo, só para o hreflang: sem artigo lá
  // (tags 5, 6 e 8 em inglês), a página do outro idioma dá 404 e o alternate não
  // pode apontar para ela. Falha aqui não muda o status: o hreflang fica.
  const semArtigoNoOutroIdioma = api
    .get(
      `v1/colunas/getColumns?search=&articletag=${articleTagId.value}&language=${outroIdioma}&limit=1&mobile=false`,
      { timeout: TIMEOUT_SSR_MS }
    )
    .then(({ data }) => semArtigo(lerRespostaColunas(data)))
    .catch(() => false);
  try {
    const { data } = await api.get(
      `v1/colunas/getColumns?search=&articletag=${articleTagId.value}&language=${
        route.params.language
      }&limit=10&mobile=${isMobile()}`
    );
    return {
      ...lerRespostaColunas(data),
      semArtigoNoOutroIdioma: await semArtigoNoOutroIdioma,
    };
  } catch {
    // Falha da API não é 404: com 503 o Google tenta de novo em vez de tirar a
    // categoria do índice. Categoria inexistente vem com 200 e `categoria: null`.
    throw createError({ statusCode: 503, statusMessage: "Service Unavailable" });
  }
});

// `fatal` para a página de erro aparecer também na navegação pelo cliente
if (error.value || !data.value) {
  throw createError({
    statusCode: 503,
    statusMessage: "Service Unavailable",
    fatal: true,
  });
}

const decisao = decidirPelaCategoria(
  data.value,
  String(route.params.catergoriaId),
  String(route.params.categoriaName)
);
if (decisao.tipo === "404") {
  throw createError({ statusCode: 404, statusMessage: "Page Not Found", fatal: true });
}
if (decisao.tipo === "301") {
  // /colunas/19/bella e /en/colunas/19/peladas-da-semana: consolida na URL
  // com o slug do idioma, sem perder a query de campanha.
  await navigateTo(
    {
      name: "language-colunas-catergoriaId-categoriaName",
      params: {
        language: route.params.language,
        catergoriaId: decisao.id,
        categoriaName: decisao.slug,
      },
      query: route.query,
    },
    { redirectCode: 301, replace: true }
  );
}

if (data.value.semArtigoNoOutroIdioma) useHreflangAusente([outroIdioma]);

const categoria = computed(() => data.value?.categoria ?? null);
const posts = computed(() => data.value?.posts ?? []);

// Nome humanizado do slug, usado como fallback quando a lista vem vazia
const humanizedSlug = computed(() =>
  String(route.params.categoriaName ?? "")
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
);

const columnName = computed(
  () =>
    categoria.value?.name ||
    (posts.value[0]?.category as string) ||
    humanizedSlug.value
);
const authorName = computed(() => posts.value[0]?.author_name as string | undefined);

const columnDescription = computed(() => {
  const metas = posts.value[0]?.meta_description ?? [];
  const langId = route.params.language === "pt-br" ? 1 : 2;
  return metas.find((m: any) => m.language_id === langId)?.content as
    | string
    | undefined;
});

const columnTitle = computed(
  () => columnName.value + (authorName.value ? " por " + authorName.value : "")
);

// H1 oculto (SEO) com o nome da categoria da coluna
const categoryH1 = computed(() =>
  route.params.language === "pt-br"
    ? `Coluna ${columnName.value} do Bella da Semana`
    : `${columnName.value} Column on Bella Club`
);

// A API intercala placeholders de banner (sem id) entre os posts; filtramos
// para o schema só listar artigos reais.
const articles = computed(() => posts.value.filter((p: any) => p?.id));

// hreflang com o slug de cada idioma (antes o /en repetia o slug em português)
function urlDaCategoria(language: string) {
  const slug =
    categoria.value?.slugs?.[language as "pt-br" | "en"] ||
    String(route.params.categoriaName);
  return (
    "https://belladasemana.com.br" +
    router.resolve({
      name: "language-colunas-catergoriaId-categoriaName",
      params: {
        language,
        catergoriaId: categoria.value?.id ?? route.params.catergoriaId,
        categoriaName: slug,
      },
    }).href
  );
}

const jsonCollectionOptions = computed(() => ({
  name: seo.value?.title || columnTitle.value,
  description:
    seo.value?.description ||
    columnDescription.value ||
    (route.params.language == "pt-br"
      ? `Fique atualizado sobre o melhor do universo masculino.`
      : `Stay updated on the best of the male universe.`),
  url: canonical,
  itemType: "Article",
  numberOfItems: articles.value.length,
  items: articles.value.map((p: any) => ({
    name: p.title,
    image: p.cover || p.img,
    url:
      "https://belladasemana.com.br" +
      router.resolve({
        name: "language-colunas-catergoriaId-categoriaName-colunaId-colunaName",
        params: {
          language: route.params.language,
          catergoriaId: p.tag_id,
          categoriaName: p.slugCategory,
          colunaId: p.id,
          colunaName: p.slug,
        },
      }).href,
  })),
}));

useHead({
  templateParams: {
    subTitle: null,
    subPage: seo.value?.title || columnTitle.value,
    page_url: canonical,
    page_description:
      seo.value?.description ||
      columnDescription.value ||
      (route.params.language == "pt-br"
        ? `Fique atualizado sobre o melhor do universo masculino.`
        : `Stay updated on the best of the male universe.`),
    page_image: shareImage(seo.value?.image),
    page_keywords:
      seo.value?.keywords ||
      "bella, bella da semana, bela da semana, bella semana, bela semana, belladasemana, bella site, bella da semana site",
    page_locale: route.params.language as string,
    page_url_pt: urlDaCategoria("pt-br"),
    page_url_en: urlDaCategoria("en"),
    page_canonical: canonical,
  },
});

const postColumns = ref(posts.value);

const limit = ref(10);
const bannerAssineHorizontal = ref("");
const search = ref("");
const searchText = ref(false);
const searchTextKeyUp = ref(false);

watch(search, (value) => {
  if (value == "") {
    searchText.value = false;
    getDados();
  }
});

onMounted(() => {
  getBannerAssineHorizontal();
});

async function getDados() {
  await api
    .get(
      `v1/colunas/getColumns?search=${search.value}&articletag=${
        articleTagId.value
      }&language=${route.params.language ?? ""}&limit=${
        limit.value
      }&mobile=${isMobile()}`
    )
    .then(({ data }) => {
      postColumns.value = data.postsColumns;
    });
  if (search.value != "") {
    searchTextKeyUp.value = true;
  }
}
async function readMore() {
  limit.value += 3;
  await getDados();
}
async function getBannerAssineHorizontal(changeLanguage = false) {
  if (!sessionStorage.getItem("bannerAssineHorizontal") || changeLanguage)
    api
      .get(
        `v1/home/getBannerSuper?mobile=${isMobile()}&language=${
          route.params.language
        }`
      )
      .then(({ data }) => {
        bannerAssineHorizontal.value = data.bannerAssineHorizontal;
        sessionStorage.setItem(
          "bannerAssineHorizontal",
          JSON.stringify(bannerAssineHorizontal.value)
        );
      });
  else
    bannerAssineHorizontal.value = JSON.parse(
      sessionStorage.getItem("bannerAssineHorizontal") ?? ""
    );
}
function getArticleTagId(data: any) {
  articleTagId.value = data.articleTagId;
  limit.value = 10;
  search.value = "";
  searchText.value = false;
  getDados();
}
function searchColumns(message: any) {
  if (search.value != "") {
    searchText.value = true;
    searchTextKeyUp.value = true;
    articleTagId.value = 9999;
    limit.value = 10;
    getDados();
  } else {
    searchText.value = false;
    return message({
      message: {
        show: true,
        title: route.params.language == "pt-br" ? "Aviso" : "Notice",
        textButton: route.params.language == "pt-br" ? "Fechar" : "Close",
        textBoby:
          route.params.language == "pt-br"
            ? "Informe algum termo para busca!"
            : "Enter a search term",
      },
    });
  }
}
function clearSearchText() {
  searchTextKeyUp.value = false;
}
</script>

<style scoped lang="css">
.slot-body {
  margin-top: 65px !important;
  background-color: #fff !important;
}

.box-gray {
  background-color: #f8f8f8;
  width: 100%;
  height: 333px;
  position: absolute;
}

.title {
  font-family: "Bebas-Neue-Light";
  color: #000000;
  font-size: 64px;
  font-weight: 300;
  letter-spacing: 0;
  line-height: 77px;
}

#search-columns {
  padding: 0 !important;
  padding-left: 30px !important;
  font-size: 1rem !important;
  background-color: #ffffff !important;
  border: 1px solid #ced4da !important;
  border-radius: 30px !important;
  height: 51px !important;
  width: 100%;
}

.box-input {
  margin-top: 6px;
}

.box-input svg {
  position: absolute;
  right: 45px;
  z-index: 9;
  color: #b20;
  cursor: pointer;
}

.subtitle {
  height: 56px;
  color: #5b5858;
  font-family: "Roboto-Medium";
  font-size: 24px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 28px;
}

.more-blog {
  font-size: 1rem !important;
  font-weight: 500;
  background-color: black !important;
  border: 1px solid black !important;
  border-radius: 30px !important;
  color: white;
}

.more-blog svg {
  color: #b20;
  margin-left: 8px;
}

.search-div {
  top: -50px;
  position: absolute;
}

@media screen and (max-width: 769px) {
  .subtitle {
    display: none;
  }

  .title {
    font-family: "Bebas-Neue-Light";
    color: #000000;
    font-size: 48px;
    font-weight: 300;
    letter-spacing: 0;
    line-height: 58px;
  }

  #search-columns {
    height: 38px !important;
    padding-left: 10px !important;
    font-size: 0.8rem !important;
    background-color: #eaeaea !important;
  }

  .box-input svg {
    right: 20px;
    z-index: 9;
  }

  .search-div {
    top: -55px;
  }
}
</style>
